import { createFetcher } from '@/services/api'
import { userService } from '@/services/userService'
import type { User } from '@/types/user'
import useSWR from 'swr'
import { useAuthToken } from './useAuthToken'

// Poll cadence while a trial learner's activation criteria are still unmet.
// The backend stamps ac_met in near-real-time (ENG-3128), but /me is otherwise
// only refetched on mount/nav, so without this a learner who crosses AC
// mid-session keeps a stale `ac_status: unmet` Userpilot trait (and its AC
// pop-ups) until they navigate. One minute bounds that staleness cheaply.
const AC_POLL_INTERVAL_MS = 60_000

/**
 * Keeps the current-user cache fresh for trial learners whose AC has not yet
 * landed, so the Userpilot `ac_status` trait (see {@link useUserpilotIdentify})
 * flips to `met` mid-session and the AC pop-ups stop.
 *
 * Shares SWR's cache key with {@link useCurrentUser}, so a revalidation here
 * updates every consumer and re-runs the identify effect. Polling is gated on
 * `trial_state === 'active' && ac_status === 'unmet'`: SWR's functional
 * `refreshInterval` returns 0 (no polling) for everyone else and stops the
 * moment AC lands, so non-trial users and already-activated learners never
 * poll.
 */
export function useTrialAcAutoRefresh(): void {
  const { getToken, isSignedIn } = useAuthToken()
  const fetcher = createFetcher(getToken)

  useSWR<User>(isSignedIn ? userService.getCurrentUserUrl() : null, fetcher, {
    revalidateOnFocus: false,
    // Never start a retry run for this key. SWR uses the config of whichever
    // instance owns the failed request, and this one carries the defaults:
    // unbounded retries backing off from 5s (10s on a connection it flags as
    // slow). That would stretch the window where {@link useCurrentUser} treats
    // the key as still-retrying far past its own short budget. Losing a poll
    // costs nothing; the next one is a minute away, and the cached user stays.
    shouldRetryOnError: false,
    refreshInterval: (data) => {
      const trial = data?.trial_summary
      const shouldPoll = trial?.trial_state === 'active' && trial.ac_status === 'unmet'
      return shouldPoll ? AC_POLL_INTERVAL_MS : 0
    },
  })
}
